import { useCallback, useEffect, useRef } from 'react'

/**
 * Общий стек открытых оверлеев: окон, панелей и просмотрщика.
 *
 * Клавиатура и блокировка прокрутки — глобальные ресурсы, и владеть ими должен
 * верхний оверлей, а не каждый по отдельности. Пока каждое окно вешало свой
 * обработчик Esc на document, одно нажатие срабатывало во всех обработчиках
 * сразу и закрывало всю стопку окон. По той же причине каждое окно независимо
 * сбрасывало `document.body.style.overflow`, и закрытие вложенного окна
 * разблокировало прокрутку под ещё открытым родителем.
 *
 * Стек живёт в модуле, а не в контексте React: оверлеи монтируются в разных
 * поддеревьях и должны видеть друг друга без общего провайдера, а состояние
 * обязано пережить размонтирование любого из них.
 */

/** Номера не переиспользуются: повторно занятый идентификатор спутал бы стек. */
let lastLayerId = 0

const stack: number[] = []

/**
 * Значение `overflow` страницы до того, как открылся первый оверлей.
 * Возвращаем именно его, а не жёсткое `'unset'`: у страницы мог быть свой стиль.
 */
let bodyOverflowBeforeLock: string | null = null

function lockBodyScroll(): void {
  if (typeof document === 'undefined') return
  bodyOverflowBeforeLock = document.body.style.overflow
  document.body.style.overflow = 'hidden'
}

function unlockBodyScroll(): void {
  if (typeof document === 'undefined') return
  document.body.style.overflow = bodyOverflowBeforeLock ?? ''
  bodyOverflowBeforeLock = null
}

function pushLayer(): number {
  lastLayerId += 1
  // Прокрутку блокирует только переход «пусто → не пусто»: промежуточные
  // открытия и закрытия её не касаются.
  if (stack.length === 0) lockBodyScroll()
  stack.push(lastLayerId)
  return lastLayerId
}

function removeLayer(id: number): void {
  const at = stack.indexOf(id)
  if (at === -1) return
  // splice, а не pop: родительское окно может закрыться раньше вложенного,
  // и тогда из стека уходит середина, а не вершина.
  stack.splice(at, 1)
  if (stack.length === 0) unlockBodyScroll()
}

/**
 * Регистрирует оверлей в общем стеке, пока он открыт, и снимает регистрацию
 * при закрытии или размонтировании.
 */
export function useOverlayLayer(isOpen: boolean): {
  /** Этот оверлей сейчас верхний. */
  isTopmost: () => boolean
} {
  const layerIdRef = useRef<number | null>(null)

  useEffect(() => {
    if (!isOpen) return
    const id = pushLayer()
    layerIdRef.current = id
    return () => {
      removeLayer(id)
      // Проверка на совпадение нужна из-за повторного запуска эффектов в
      // StrictMode: чужой, более свежий номер затирать нельзя.
      if (layerIdRef.current === id) layerIdRef.current = null
    }
  }, [isOpen])

  // Функция, а не готовое значение: обработчик клавиатуры подписывается один
  // раз и должен видеть стек на момент нажатия, а не на момент подписки.
  const isTopmost = useCallback(
    () => layerIdRef.current !== null && stack[stack.length - 1] === layerIdRef.current,
    [],
  )

  return { isTopmost }
}

/** Только для тестов: текущая глубина стека. */
export function overlayStackDepth(): number {
  return stack.length
}
